import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import React, { useState, useEffect } from 'react';

import { RoomList } from '../room-list/room-list';

import { MenuPopover } from './menu-popover';

import { useAppContext } from '@/hooks/use-app-context';

/**
 * ヘッダーUIコンポーネントのプロパティ
 */
export interface HeaderProps {
  /** infoボタンがクリックされたときのコールバック */
  onInfoClick?: () => void;
  /** 権利表記ボタンがクリックされたときのコールバック */
  onLicenseClick?: () => void;
  /** 操作説明ボタンがクリックされたときのコールバック */
  onOperationGuideClick?: () => void;
  /** クオリティ調整ボタンがクリックされたときのコールバック */
  onQualityClick?: () => void;
  /** 強制追従ボタンがクリックされたときのコールバック */
  onForceSyncClick?: () => void;
  /** ホストモードかどうか */
  hostMode?: boolean;
  /** ゲストモードかどうか */
  guestMode?: boolean;
}

/**
 * ヘッダーUIコンポーネント
 *
 * 黒い半透明のバーの上にルーム名とその隣にinfoボタン、右端にmenuボタンを表示します。
 */
export function Header({
  onInfoClick,
  onLicenseClick,
  onOperationGuideClick,
  onQualityClick,
  onForceSyncClick,
  hostMode,
  guestMode
}: HeaderProps) {
  // メニューポップオーバーの表示状態
  const [showMenu, setShowMenu] = useState(false);
  // ルーム一覧の表示状態
  const [showRoomList, setShowRoomList] = useState(false);
  // フォントの読み込み状態
  const [fontLoaded, setFontLoaded] = useState(false);
  // 全員追従ボタンのツールチップ表示状態
  const [showForceSyncTooltip, setShowForceSyncTooltip] = useState(false);

  // GoogleフォントのMaterial Symbols（Rounded版）をプリロードする
  useEffect(() => {
    // フォントをプリロードするためのlinkタグを作成
    const preloadLink = document.createElement('link');
    preloadLink.href =
      'https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200';
    preloadLink.rel = 'preload';
    preloadLink.as = 'style';
    document.head.appendChild(preloadLink);

    // フォントを実際に読み込むためのlinkタグを作成
    const styleLink = document.createElement('link');
    styleLink.href =
      'https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200';
    styleLink.rel = 'stylesheet';
    document.head.appendChild(styleLink);

    // フォントの読み込みが完了したらステートを更新
    styleLink.onload = () => {
      setFontLoaded(true);
    };

    // 念のため、タイムアウトを設定して、フォントが読み込まれなくても表示されるようにする
    const timeoutId = setTimeout(() => {
      setFontLoaded(true);
    }, 1000);

    return () => {
      document.head.removeChild(preloadLink);
      document.head.removeChild(styleLink);
      clearTimeout(timeoutId);
    };
  }, []);

  // アプリコンテキストからルーム情報と同期状態を取得
  const { getCurrentRoom, state, dispatch } = useAppContext();
  const { isSyncEnabled } = state;
  const currentRoom = getCurrentRoom();
  const roomName = currentRoom?.name || 'ルーム未選択';

  // 固定スタイル設定
  const headerHeight = '78px';
  const titleFontSize = '23px';
  const infoIconSize = 32;
  const buttonFontSize = '20px';

  // メニューボタンがクリックされたときのハンドラー
  const handleMenuClick = () => {
    setShowMenu(!showMenu);
  };

  // 権利表記メニューがクリックされたときのハンドラー
  const handleLicenseClick = () => {
    setShowMenu(false); // メニューを閉じる
    if (onLicenseClick) {
      onLicenseClick();
    }
  };

  // 操作説明メニューがクリックされたときのハンドラー
  const handleOperationGuideClick = () => {
    setShowMenu(false); // メニューを閉じる
    if (onOperationGuideClick) {
      onOperationGuideClick();
    }
  };

  // クオリティ調整メニューがクリックされたときのハンドラー
  const handleQualityClick = () => {
    setShowMenu(false); // メニューを閉じる
    if (onQualityClick) {
      onQualityClick();
    }
  };

  // プレビューモードへメニューがクリックされたときのハンドラー（将来使用する可能性があるため残す）
  // const handlePreviewModeClick = () => {
  //   setShowMenu(false); // メニューを閉じる
  //   // 現在のルームのエリアを取得
  //   const currentRoomArea = currentRoom?.area;
  //   if (currentRoomArea) {
  //     // エリアが設定されている場合、そのエリアのプレビューモードへ遷移
  //     window.location.href = `?area=${currentRoomArea}&mode=preview`;
  //   } else {
  //     // エリアが設定されていない場合、全ルームのプレビューモード（仕様未定）
  //     alert('このルームにはエリアが設定されていません');
  //   }
  // };

  // ルーム一覧ボタンがクリックされたときのハンドラー
  const handleRoomListClick = () => {
    const newState = !showRoomList;
    setShowRoomList(newState);
    // アプリケーションの状態も更新
    dispatch({ type: 'SET_ROOM_LIST_VISIBLE', payload: newState });
  };

  // ヘッダーのスタイル定義
  const headerStyle = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: headerHeight,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const, // centerからspace-betweenに戻す
    padding: '0 0px',
    color: 'white',
    fontFamily: 'sans-serif',
    zIndex: 1000
  };

  return (
    <>
      <div style={headerStyle}>
        {/* 左側のルーム一覧ボタン */}
        <div style={{ width: '25%', display: 'flex', justifyContent: 'flex-start', paddingLeft: '88px' }}>
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: buttonFontSize
            }}
            onClick={handleRoomListClick}
          >
            {showRoomList ? (
              <ExpandLessRoundedIcon style={{ fontSize: 24, marginRight: '4px' }} />
            ) : (
              <ExpandMoreRoundedIcon style={{ fontSize: 24, marginRight: '4px' }} />
            )}
            ルーム一覧
          </button>
        </div>

        {/* 中央のタイトルとinfoボタン */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '50%', // 中央部分の幅を50%に設定
            overflow: 'hidden' // はみ出した部分を隠す
          }}
        >
          {/* ルーム名とinfoボタンをグループ化 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              maxWidth: '100%'
            }}
          >
            <div
              title={roomName}
              style={{
                fontSize: titleFontSize,
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '360px', // ルーム名の最大幅を360pxに制限
                marginRight: '6px'
              }}
            >
              {roomName}
            </div>
            <button
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                visibility: fontLoaded ? 'visible' : 'hidden' // フォントが読み込まれるまで非表示
              }}
              onClick={onInfoClick}
            >
              <span
                className='material-symbols-rounded'
                style={{
                  color: 'white',
                  fontSize: infoIconSize,
                  fontFamily: 'Material Symbols Rounded',
                  fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24"
                }}
              >
                info
              </span>
            </button>
          </div>
        </div>

        {/* 右側のボタン群 */}
        <div style={{ width: '25%', display: 'flex', justifyContent: 'flex-end', paddingRight: '88px', gap: '8px' }}>
          {/* 強制追従ボタン（ホストモードの時のみ表示） */}
          {hostMode && (
            <div style={{ position: 'relative' }}>
              <button
                style={{
                  backgroundColor: 'rgba(19, 191, 131, 0.9)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '100px',
                  padding: '8px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  whiteSpace: 'nowrap'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onForceSyncClick) {
                    onForceSyncClick();
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(19, 191, 131, 1)';
                  setShowForceSyncTooltip(true);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(19, 191, 131, 0.9)';
                  setShowForceSyncTooltip(false);
                }}
              >
                全員追従ON
              </button>
              {/* カスタムツールチップ */}
              {showForceSyncTooltip && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-40px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.87)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    zIndex: 10000,
                    pointerEvents: 'none',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  全ゲストの「ホスト操作に追従」を強制的にオンにします
                </div>
              )}
            </div>
          )}

          {/* メニューボタン */}
          <button
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              visibility: fontLoaded ? 'visible' : 'hidden' // フォントが読み込まれるまで非表示
            }}
            onClick={handleMenuClick}
          >
            <span
              className='material-symbols-rounded'
              style={{
                color: 'white',
                fontSize: 24,
                fontFamily: 'Material Symbols Rounded',
                fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24"
              }}
            >
              more_vert
            </span>
          </button>

          {/* メニューポップオーバー */}
          <MenuPopover
            visible={showMenu}
            onClose={() => setShowMenu(false)}
            onLicenseClick={handleLicenseClick}
            onOperationGuideClick={handleOperationGuideClick}
            onQualityClick={handleQualityClick}
          />
        </div>
      </div>

      {/* ルーム一覧コンポーネント */}
      <RoomList
        isHost={hostMode}
        isSyncEnabled={isSyncEnabled}
        isTourMode={hostMode || guestMode} // ツアーモードはホストモードまたはゲストモード
        visible={showRoomList}
        onClose={() => {
          setShowRoomList(false);
          // ルームリストが閉じられたときにアプリケーションの状態も更新
          dispatch({ type: 'SET_ROOM_LIST_VISIBLE', payload: false });
        }}
      />
    </>
  );
}
